import { call, put, takeLatest } from 'redux-saga/effects';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import {
    createManualInvoice,
    manualInvoiceCreated,
    manualInvoiceCreationFailed,
} from '../invoices';
import { showToastAction } from '../ui';
import moment from 'moment';
import { getTimeZone } from 'src/utils/dateTime';

/**
 * Saga effect to handle manual invoice creation
 * @param {Object} payload - Form data for creating manual invoice
 */
function* createManualInvoiceEffect({ payload }) {
    try {
        // Extract data from payload (bindActionToPromise wraps it in {data, resolve, reject})
        const formData = payload.data || payload;

        // Convert time values to EPOCH timestamps using entity timezone
        const timeZone = getTimeZone(
            formData.companyDetails?.address?.province,
        );
        const shiftDate = moment(formData.shiftDate).format('YYYY-MM-DD');
        const shiftStartTime = moment
            .tz(shiftDate, timeZone)
            .set('hours', Math.floor(formData.shiftStartTime))
            .set('minutes', Math.round((formData.shiftStartTime % 1) * 60))
            .set('seconds', 0)
            .set('milliseconds', 0)
            .format('X');
        const shiftEndTime = moment
            .tz(shiftDate, timeZone)
            .set('hours', Math.floor(formData.shiftEndTime))
            .set('minutes', Math.round((formData.shiftEndTime % 1) * 60))
            .set('seconds', 0)
            .set('milliseconds', 0)
            .format('X');

        const requestBody = {
            entityId:
                typeof formData.company === 'string'
                    ? formData.company
                    : formData.company?._id || formData.company?.id,
            skill: formData.skill,
            subSkills: formData.subSkills || [],
            date: formData.shiftDate,
            shiftStartTime: shiftStartTime,
            shiftEndTime: shiftEndTime,
            vacancies: formData.vacancies,
            rate: formData.shiftBaseRate,
            stafferRate: formData.payContractorADifferentHourlyRate
                ? Number(formData.stafferRate)
                : undefined,
            unpaidBreak: formData.unpaidBreak,
            isTipIncluded: formData.plusTips,
            travelTime: formData.transitAllowance || 0,
            isTrainingShift: formData.isATrainingShift,
            unitNumber: formData.unitNumber,
            disclaimer: formData.disclaimer,
            shiftNotes: formData.shiftNotes,
            internalNotes: formData.internalNotes,
            reference: formData.internalReference,
            contractors: formData.contractors.map(c => ({
                contractorId: c.contractor?._id || c.contractor?.id,
                trackedTime: c.trackedTime,
            })),
        };

        // The Shift Address dropdown stores the address _id (a string), not the
        // address object. Resolve it back to the full object before sending, so
        // the backend's Joi object validation passes (mirrors the broadcast flow).
        let resolvedCompanyAddress = formData.companyAddress;
        if (
            resolvedCompanyAddress &&
            typeof resolvedCompanyAddress === 'string'
        ) {
            const allAddresses = [
                formData.companyDetails?.address,
                ...(formData.companyDetails?.creator?.otherAddresses ?? []),
            ];
            resolvedCompanyAddress =
                allAddresses.find(
                    a =>
                        a?._id === formData.companyAddress ||
                        a?.street === formData.companyAddress,
                ) || null;
        }

        // Include selected address if available
        if (resolvedCompanyAddress) {
            requestBody.companyAddress = resolvedCompanyAddress;
        } else if (formData.shiftAddress) {
            const selectedAddress =
                formData.companyDetails?.creator?.otherAddresses?.find(
                    item => item._id === formData.shiftAddress,
                );
            if (selectedAddress) {
                requestBody.companyAddress = selectedAddress;
            } else {
                requestBody.shiftAddress = formData.shiftAddress;
            }
        } else if (formData.companyDetails?.address) {
            requestBody.companyAddress = formData.companyDetails.address;
        }

        const response = yield call(SendApiRequest, {
            url: apiRoutes.createManualInvoice,
            method: 'POST',
            body: requestBody,
        });

        // Dispatch success action
        yield put(
            manualInvoiceCreated({
                job: response.data?.job,
                invoice: response.data?.invoice,
                trackers: response.data?.trackers || [],
            }),
        );

        // Show success toast
        yield put(
            showToastAction({
                type: 'success',
                message: 'Manual invoice created successfully!',
            }),
        );

        // Call success callback if provided
        if (formData.onSuccess && typeof formData.onSuccess === 'function') {
            formData.onSuccess(response.data);
        }

        // Call resolve if using bindActionToPromise
        if (payload.resolve && typeof payload.resolve === 'function') {
            payload.resolve(response.data);
        }
    } catch (error) {
        console.error('Error creating manual invoice:', error);

        // Extract data from payload
        const formData = payload.data || payload;

        const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            'Failed to create manual invoice';

        // Dispatch failure action
        yield put(
            manualInvoiceCreationFailed({
                error: errorMessage,
            }),
        );

        // Show error toast
        yield put(
            showToastAction({
                type: 'error',
                message: errorMessage,
            }),
        );

        // Call error callback if provided
        if (formData.onError && typeof formData.onError === 'function') {
            formData.onError(error);
        }

        // Call reject if using bindActionToPromise
        if (payload.reject && typeof payload.reject === 'function') {
            payload.reject(error);
        }
    }
}

// Watcher Saga: listens for the createManualInvoice action
export const invoicesSagas = [
    takeLatest(createManualInvoice.type, createManualInvoiceEffect),
];
